import axios from 'axios';

// Use relative URL to avoid CORS entirely.
// The Kubernetes ingress routes /api/* to the backend on the same domain.
// This works on qanunlegal.com, preview URL, and localhost alike.
export const API = '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const api = axios.create({
  baseURL: API,
});

export const authAPI = {
  processSession: (sessionId, redirectOrigin) =>
    api.post('/auth/session', { session_id: sessionId, redirect_origin: redirectOrigin }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

export const chatAPI = {
  getConversations: () => api.get('/chat/conversations'),
  getMessages: (conversationId) => api.get(`/chat/conversations/${conversationId}/messages`),
  sendMessage: (data) => api.post('/chat/send', data),
};

export const documentsAPI = {
  list: () => api.get('/documents/list'),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/documents/upload', formData);
  },
  analyze: (documentId) => api.get(`/documents/analyze/${documentId}`),
};

export const contractsAPI = {
  getTemplates: () => api.get('/contracts/templates'),
  generate: (data) => api.post('/contracts/generate', data),
  generatePublic: (data) => api.post('/contracts/generate-public', data),
  list: () => api.get('/contracts/list'),
};

export const searchAPI = {
  search: (query, jurisdiction) => 
    api.get('/search/legal', { params: { q: query, jurisdiction } }),
};

export const alertsAPI = {
  list: (jurisdiction, category) => 
    api.get('/alerts/list', { params: { jurisdiction, category } }),
};

export const paymentsAPI = {
  createCheckoutSession: (packageId) =>
    api.post('/payments/checkout/session', { package_id: packageId }),
  getCheckoutStatus: (sessionId) =>
    api.get(`/payments/checkout/status/${sessionId}`),
};

export const userAPI = {
  getSubscription: () => api.get('/user/subscription'),
  updateProfile: (data) => api.patch('/user/profile', data),
};

export const researchAPI = {
  save: (data) => api.post('/research/save', data),
  getHistory: (params) => api.get('/research/history', { params }),
  toggleSave: (researchId) => api.patch(`/research/${researchId}/toggle-save`),
  delete: (researchId) => api.delete(`/research/${researchId}`),
  createFolder: (name) => api.post('/research/folders/create', { name }),
  getFolders: () => api.get('/research/folders'),
  moveToFolder: (researchId, folderId) => api.post('/research/move-to-folder', { research_id: researchId, folder_id: folderId }),
  deleteFolder: (folderId) => api.delete(`/research/folders/${folderId}`),
};

export const compareAPI = {
  compareJurisdictions: (data) => api.post('/compare/jurisdictions', data),
};

export const shareAPI = {
  create: (data) => api.post('/share/create', data),
  get: (shareId) => api.get(`/share/${shareId}`),
};

export const analyticsAPI = {
  getOverview: () => api.get('/analytics/overview'),
};

export const briefsAPI = {
  generate: (data) => api.post('/briefs/generate', data),
};

export const freezonesAPI = {
  list: (params) => api.get('/freezones/list', { params }),
  get: (id) => api.get(`/freezones/${id}`),
  compare: (id1, id2) => api.get(`/freezones/compare/${id1}/${id2}`),
};

export const clausesAPI = {
  list: (params) => api.get('/clauses/list', { params }),
  get: (id) => api.get(`/clauses/${id}`),
};

export const timelineAPI = {
  topics: () => api.get('/timeline/topics'),
  get: (topicId) => api.get(`/timeline/${topicId}`),
};

export const casesAPI = {
  search: (params) => api.get('/cases/search', { params }),
  get: (id) => api.get(`/cases/${id}`),
};

export const contractReviewAPI = {
  review: (data) => api.post('/contracts/review', data),
};

export const portalAPI = {
  create: (data) => api.post('/portal/create', data),
  get: (portalId) => api.get(`/portal/${portalId}`),
  list: () => api.get('/portal/my-portals'),
};

export const strategyAPI = {
  create: (data) => api.post('/strategy/create', data),
  list: () => api.get('/strategy/list'),
  get: (sessionId) => api.get(`/strategy/${sessionId}`),
  addMilestone: (sessionId, data) => api.post(`/strategy/${sessionId}/milestone`, data),
  sendMessage: (sessionId, data) => api.post(`/strategy/${sessionId}/message`, data),
};

export const enterpriseAPI = {
  createApiKey: (data) => api.post('/enterprise/api-keys/create', data),
  listApiKeys: () => api.get('/enterprise/api-keys'),
  revokeApiKey: (keyId) => api.delete(`/enterprise/api-keys/${keyId}`),
  configureSso: (data) => api.post('/enterprise/sso/configure', data),
  getSsoStatus: () => api.get('/enterprise/sso/status'),
  getUsage: () => api.get('/enterprise/usage'),
  verifySso: (domain) => api.post('/enterprise/sso/verify', null, { params: { domain } }),
  initiateSso: (domain) => api.get(`/enterprise/sso/initiate/${domain}`),
  getSsoMetadata: (domain) => api.get('/enterprise/sso/metadata', { params: { domain } }),
  getSsoSessions: () => api.get('/enterprise/sso/sessions'),
  getAuditLog: (limit) => api.get('/enterprise/audit-log', { params: { limit } }),
};

export const ocrAPI = {
  extract: (formData) => api.post('/ocr/extract', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  analyze: (formData) => api.post('/ocr/analyze', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
};

export const negotiateAPI = {
  create: (data) => api.post('/negotiate/create', data),
  list: () => api.get('/negotiate/list'),
  get: (sessionId) => api.get(`/negotiate/${sessionId}`),
  sendMessage: (sessionId, data) => api.post(`/negotiate/${sessionId}/message`, data),
  propose: (sessionId, data) => api.post(`/negotiate/${sessionId}/propose`, data),
  respondProposal: (sessionId, changeId, data) => api.patch(`/negotiate/${sessionId}/propose/${changeId}`, data),
};

export const workspaceAPI = {
  create: (name) => api.post('/workspaces/create', { name }),
  list: () => api.get('/workspaces/list'),
  get: (workspaceId) => api.get(`/workspaces/${workspaceId}`),
  getFeed: (workspaceId) => api.get(`/workspaces/${workspaceId}/feed`),
  share: (workspaceId, data) => api.post(`/workspaces/${workspaceId}/share`, data),
  invite: (workspaceId, email) => api.post('/workspaces/invite', { workspace_id: workspaceId, email }),
  acceptInvite: (inviteId) => api.post(`/workspaces/invites/${inviteId}/accept`),
  getStats: (workspaceId) => api.get(`/workspaces/${workspaceId}/stats`),
};

export const marketplaceAPI = {
  list: (params) => api.get('/marketplace/templates', { params }),
  get: (id) => api.get(`/marketplace/templates/${id}`),
  getReviews: (id) => api.get(`/marketplace/templates/${id}/reviews`),
  addReview: (id, data) => api.post(`/marketplace/templates/${id}/review`, data),
  purchase: (id) => api.post(`/marketplace/templates/${id}/purchase`),
  checkout: (id) => api.post(`/marketplace/templates/${id}/checkout`),
  checkoutStatus: (id, checkoutId) => api.get(`/marketplace/templates/${id}/checkout/${checkoutId}`),
  submit: (data) => api.post('/marketplace/templates/submit', data),
  categories: () => api.get('/marketplace/categories'),
  businessTypes: () => api.get('/marketplace/business-types'),
  getVersions: (id) => api.get(`/marketplace/templates/${id}/versions`),
  createVersion: (id, data) => api.post(`/marketplace/templates/${id}/versions`, data),
  customize: (data) => api.post('/marketplace/templates/customize', data),
  startCollab: (id, data) => api.post(`/marketplace/templates/${id}/collab/start`, data),
  submitCollabEdit: (id, collabId, data) => api.post(`/marketplace/templates/${id}/collab/${collabId}/edit`, data),
  getCollab: (id, collabId) => api.get(`/marketplace/templates/${id}/collab/${collabId}`),
  activeCollabs: () => api.get('/marketplace/collab/active'),
};

export const alertsLiveAPI = {
  getLive: (params) => api.get('/alerts/live', { params }),
  subscribe: (data) => api.post('/alerts/subscribe', data),
  listSubscriptions: (email) => api.get('/alerts/subscriptions', { params: { email } }),
  streamUrl: () => `${api.defaults.baseURL}/alerts/stream`,
};

export const nlpAPI = {
  extract: (data) => api.post('/nlp/extract', data),
  analyze: (data) => api.post('/nlp/analyze', data),
  legalTerms: () => api.get('/nlp/legal-terms'),
};

export const whitelabelAPI = {
  getConfig: (orgId) => api.get('/whitelabel/config', { params: { org_id: orgId } }),
  getOrgConfig: (orgId) => api.get(`/whitelabel/config/${orgId}`),
  updateConfig: (data) => api.post('/whitelabel/config', data),
  deleteConfig: (orgId) => api.delete(`/whitelabel/config/${orgId}`),
  listOrgs: () => api.get('/whitelabel/orgs'),
  getThemes: () => api.get('/whitelabel/themes'),
  previewTheme: (data) => api.post('/whitelabel/preview', data),
};

export const collabAPI = {
  createSession: (data) => api.post('/collab/sessions/create', data),
  getSession: (collabId) => api.get(`/collab/sessions/${collabId}`),
  listSessions: (status) => api.get('/collab/sessions', { params: { status } }),
  invite: (collabId, data) => api.post(`/collab/sessions/${collabId}/invite`, data),
  wsUrl: (collabId) => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${window.location.host}/api/collab/ws/${collabId}`;
  },
};

export const compareDocsAPI = {
  compare: (data) => api.post('/compare-docs/compare', data),
  history: () => api.get('/compare-docs/history'),
  samples: () => api.get('/compare-docs/samples'),
  getSample: (id) => api.get(`/compare-docs/samples/${id}`),
};

export const complianceAPI = {
  check: (data) => api.post('/compliance/check', data),
  regulations: () => api.get('/compliance/regulations'),
  history: () => api.get('/compliance/history'),
  businessTypes: () => api.get('/compliance/business-types'),
};

export const wlMarketplaceAPI = {
  listings: (params) => api.get('/wl-marketplace/listings', { params }),
  getListing: (id) => api.get(`/wl-marketplace/listings/${id}`),
  createListing: (data) => api.post('/wl-marketplace/listings', data),
  requestAccess: (data) => api.post('/wl-marketplace/request-access', data),
  requests: (listingId) => api.get('/wl-marketplace/requests', { params: { listing_id: listingId } }),
  stats: () => api.get('/wl-marketplace/stats'),
};
